import React, { useEffect, useState } from "react";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { PageRenderer } from "./PageRenderer";
import { PageBuilder as PageBuilderComponent } from "./PageBuilder";
import { PageBuilderProvider } from "../../context/PageBuilderContext";
import { IComponentNode, PageType } from "../../types/pageBuilder.types";
import { FaEdit } from "react-icons/fa";
import {
 clearCachedPageBuilderPageByType,
 getCachedPageBuilderPageByType,
 setCachedPageBuilderPageByType,
} from "../../utils/pageBuilderPageCache";

interface PageZoneProps {
 zoneName: string;
 pageType: PageType;
 fallback?: React.ReactNode;
}

interface IPageBuilderPageByTypeResponse {
 page?: {
  _id: string;
  zones?: Record<string, IComponentNode>;
 };
}

// Helper function to check if a zone is empty
const isZoneEmpty = (zone: IComponentNode | null): boolean => {
 if (!zone) return true;
 
 // Check if it's a container with no children or only empty containers
 if (zone.type === "container" || zone.type === "grid") {
  if (!zone.children || zone.children.length === 0) {
   return true;
  }
  // Check if all children are empty containers
  return zone.children.every((child) => isZoneEmpty(child));
 }
 
 // For other component types, check if they have meaningful content
 if (zone.type === "text" || zone.type === "heading") {
  return !zone.props?.text || zone.props.text.trim() === "";
 }
 
 if (zone.type === "image") {
  return !zone.props?.src;
 }
 
 return false;
};

export const PageZone: React.FC<PageZoneProps> = ({ zoneName, pageType, fallback }) => {
 const { api } = useApi();
 const { user } = useAuth();
 const [zoneContent, setZoneContent] = useState<IComponentNode | null>(null);
 const [loading, setLoading] = useState(true);
 const [pageId, setPageId] = useState<string | null>(null);
 const [isHovered, setIsHovered] = useState(false);
 const [isEditing, setIsEditing] = useState(false);

 const isStoreAdmin = user?.role === "admin" || user?.role === "store_admin" || user?.role === "co_admin";

 useEffect(() => {
  loadZoneContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [pageType, zoneName]);

 const applyPageToZoneState = (page: IPageBuilderPageByTypeResponse["page"] | undefined): void => {
  if (page?._id) {
   setPageId(page._id);
   const zones = page.zones || {};
   if (zones[zoneName]) {
    setZoneContent(zones[zoneName]);
    return;
   }
   setZoneContent({
    id: `zone-${zoneName}-${Date.now()}`,
    type: "container",
    props: {},
    styles: { default: {} },
    children: [],
   });
   return;
  }
  setZoneContent(null);
  setPageId(null);
 };

 const loadZoneContent = async () => {
  try {
   setLoading(true);

   const cached = getCachedPageBuilderPageByType<IPageBuilderPageByTypeResponse>(pageType);
   if (cached?.page) {
    applyPageToZoneState(cached.page);
    return;
   }

   const response = await api.get(`/page-builder/pages/type/${pageType}`);
   const data = response.data as IPageBuilderPageByTypeResponse;
   setCachedPageBuilderPageByType<IPageBuilderPageByTypeResponse>(pageType, data);
   applyPageToZoneState(data.page);
  } catch (error) {
   // Page not found - use fallback
   setZoneContent(null);
   setPageId(null);
  } finally {
   setLoading(false);
  }
 };

 const handleEditClick = () => {
  setIsEditing(true);
 };

 const handleSaveZone = async (layout: IComponentNode) => {
  if (!pageId) return;

  try {
   const response = await api.get(`/page-builder/pages/${pageId}`);
   const page = response.data.page;
   const currentZones = { ...(page.zones || {}) };
   currentZones[zoneName] = layout;

   await api.put(`/page-builder/pages/${pageId}`, {
    zones: currentZones,
   });

   // Invalidate cached page-by-type so all zones pick up the latest content
   clearCachedPageBuilderPageByType(pageType);

   // Reload zone content
   await loadZoneContent();
   setIsEditing(false);
  } catch (err: any) {
   console.error("Error saving zone:", err);
   alert(err.response?.data?.error || "Failed to save zone");
  }
 };

 const handleCancelEdit = () => {
  setIsEditing(false);
 };

 if (loading) {
  return <>{fallback || null}</>;
 }

 // Zone editor modal
 if (isEditing && zoneContent && pageId) {
  return (
   <PageBuilderProvider initialLayout={zoneContent}>
    <div className="fixed inset-0 flex flex-col bg-gray-100 z-[9999]">
     <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
      <div>
       <h2 className="text-lg font-semibold text-gray-900">
        Edit Zone: {zoneName}
       </h2>
       <p className="text-sm text-gray-500 mt-1">
        Page Type: <strong>{pageType}</strong>
       </p>
      </div>
      <button
       onClick={handleCancelEdit}
       className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
      >
       Cancel
      </button>
     </div>
     <div className="flex-1 overflow-hidden">
      <PageBuilderComponent
       initialLayout={zoneContent}
       onCancel={handleCancelEdit}
       onSave={handleSaveZone}
       zoneName={zoneName}
       pageType={pageType}
      />
     </div>
    </div>
   </PageBuilderProvider>
  );
 }

 // Always render zone container, even if empty, so it's visible and editable
 // Only return null/fallback if page doesn't exist at all
 if (!zoneContent && !pageId) {
  return <>{fallback || null}</>;
 }

 // If zone doesn't exist but page does, create empty zone structure for rendering
 const zoneToRender: IComponentNode = zoneContent || {
  id: `zone-${zoneName}-empty`,
  type: "container",
  props: {},
  styles: { default: {} },
  children: [],
 };
 
 const isEmptyToRender = isZoneEmpty(zoneToRender);

 return (
  <div
   className={`relative group ${isStoreAdmin ? "cursor-pointer" : ""} ${isEmptyToRender ? "min-h-[120px] py-8" : ""}`}
   onMouseEnter={() => setIsHovered(true)}
   onMouseLeave={() => setIsHovered(false)}
   onClick={isStoreAdmin && isEmptyToRender ? handleEditClick : undefined}
  >
   {isStoreAdmin && !isEditing && !isEmptyToRender && isHovered && (
    <button
     onClick={(e) => {
      e.stopPropagation();
      handleEditClick();
     }}
     className="absolute top-2 right-2 z-50 bg-blue-500 text-white p-2  hover:bg-blue-600 transition-colors flex items-center gap-2"
     title={`Edit ${zoneName} zone`}
    >
     <FaEdit className="text-sm" />
     <span className="text-xs font-medium">Edit Zone</span>
    </button>
   )}
   <div
    className={`relative ${
     isStoreAdmin
      ? `border-2 border-dashed transition-all ${
        isEmptyToRender
         ? "border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/30 min-h-[100px]"
         : "border-transparent group-hover:border-blue-300"
       }`
      : ""
    }`}
   >
    {isEmptyToRender && isStoreAdmin && (
     <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
      <div className="text-center text-gray-400 text-sm">
       <p className="font-medium mb-1">Empty {zoneName} zone</p>
       <p className="text-xs">Click anywhere to edit</p>
      </div>
     </div>
    )}
    <div className={isEmptyToRender ? "opacity-30" : ""}>
     <PageRenderer layout={zoneToRender} isPreview={false} />
    </div>
   </div>
  </div>
 );
};

